/**
 * Inbox HTTP API.
 *
 * Mirrors the structure of `frontend/src/api/services/campaigns.api.ts` but is
 * module-scoped under `features/inbox/api/` (the inbox is self-contained;
 * keeping the API surface next to its consumers avoids cross-feature drift).
 *
 * Every call uses the shared axios client at `frontend/src/api/client.ts`
 * which already attaches the JWT and handles 401 refresh.
 */
import { apiClient } from '@/api/client';
import { NOTE_ATTACHMENT_PREVIEW_TIMEOUT_MS } from '../constants/note-attachments';
import type { IApiResponse } from '@/api/types';
import type {
    MessageChannel,
    ConversationStatus,
    IAiDraftInput,
    IAiDraftResult,
    IArchiveUnifiedInput,
    IAssignUnifiedInput,
    IComposeEmailInput,
    IComposeEmailResult,
    IComposeSmsInput,
    IComposeSmsResult,
    IComposeWhatsAppInput,
    IComposeWhatsAppResult,
    IContactFilterCandidate,
    IConversationNote,
    IEmailAttachmentUploadUrl,
    IEmailDraft,
    IInboxFilters,
    IListMessagesResponse,
    IListUnifiedResponse,
    IMentionCandidate,
    IMessageAttachment,
    INoteAttachmentPreview,
    INoteAttachmentUploadUrl,
    IMuteUnifiedInput,
    IPinUnifiedInput,
    IRescheduleMessageInput,
    IResolveUnifiedInput,
    IResolveUnifiedResult,
    IScheduleMessageInput,
    IStarredMessage,
    ISendMessageAck,
    ISendInternalNoteInput,
    ISendMessageInput,
    ISetStatusUnifiedInput,
    ISyncRequestInput,
    IConversationInsight,
    ISyncResponse,
    IUnifiedByContactResult,
    IUnifiedConversation,
    IUpdateInternalNoteInput,
    IUpsertEmailDraftInput,
    IEmailThread,
} from '../types';

const ROOT = '/inbox';

/**
 * The picker's three mutually exclusive lookup modes. Mirrors the backend's
 * discriminated union — sending two at once is a 400, not an AND.
 */
export type IContactFilterLookup =
    | { readonly email: string }
    | { readonly phone: string }
    | { readonly ids: readonly string[] };

/**
 * Stream B will define the cursor / pagination DTOs. Until then we pass the
 * filters as flat query params and accept any of: `nextCursor`, `before`,
 * `since`. Each call site documents its expected shape.
 */
function flattenFiltersToParams(filters: IInboxFilters): Record<string, unknown> {
    const params: Record<string, unknown> = { tab: filters.tab, sortOrder: filters.sortOrder };
    if (filters.channel.length > 0) params.channel = filters.channel;
    if (filters.assignedTo) params.assignedTo = filters.assignedTo;
    if (filters.tagIds.length > 0) params.tagIds = filters.tagIds;
    if (filters.contactIds.length > 0) params.contactIds = filters.contactIds;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (typeof filters.hasUnread === 'boolean') params.hasUnread = filters.hasUnread;
    if (filters.status.length > 0) params.status = filters.status;
    if (filters.stage.length > 0) params.stage = filters.stage;
    if (typeof filters.isPinned === 'boolean') params.isPinned = filters.isPinned;
    if (filters.isStarred === true) params.isStarred = true;
    if (filters.replyState) params.replyState = filters.replyState;
    if (filters.q) params.q = filters.q;
    return params;
}

export const inboxApi = {
    listUnifiedConversations: (
        filters: IInboxFilters,
        cursor?: string,
    ): Promise<IListUnifiedResponse> =>
        apiClient
            .get<IApiResponse<IListUnifiedResponse>>(`${ROOT}/unified-conversations`, {
                params: {
                    ...flattenFiltersToParams(filters),
                    ...(cursor ? { cursor } : {}),
                },
            })
            .then((r) => r.data.data),

    /**
     * Contacts for the filter drawer's pickers. Exactly one of `email` /
     * `phone` / `ids` — the backend rejects an ambiguous combination. `phone`
     * must already be national digits (see `toNationalDigits`).
     */
    listContactFilterCandidates: (
        lookup: IContactFilterLookup,
        limit?: number,
    ): Promise<IContactFilterCandidate[]> =>
        apiClient
            .get<IApiResponse<IContactFilterCandidate[]>>(`${ROOT}/contact-filter-candidates`, {
                params: { ...lookup, ...(limit ? { limit } : {}) },
            })
            .then((r) => r.data.data),

    getUnifiedConversation: (id: string): Promise<IUnifiedConversation> =>
        apiClient
            .get<IApiResponse<IUnifiedConversation>>(`${ROOT}/unified-conversations/${id}`)
            .then((r) => r.data.data),

    /**
     * The contact's most recently active unified conversation, or null. Pure
     * lookup for the contact-drawer compose (feeds the AI draft's conversation
     * context) — creation happens only via `resolveUnifiedConversation`.
     */
    getUnifiedByContact: (contactId: string): Promise<IUnifiedByContactResult> =>
        apiClient
            .get<
                IApiResponse<IUnifiedByContactResult>
            >(`${ROOT}/unified-conversations/by-contact/${contactId}`)
            .then((r) => r.data.data),

    /**
     * Find-or-create the unified conversation for a contact. Call immediately
     * before send/schedule — never at compose open (an empty shell would sit
     * at the top of the inbox list until its first message lands).
     */
    resolveUnifiedConversation: (body: IResolveUnifiedInput): Promise<IResolveUnifiedResult> =>
        apiClient
            .post<
                IApiResponse<IResolveUnifiedResult>
            >(`${ROOT}/unified-conversations/resolve`, body)
            .then((r) => r.data.data),

    /**
     * The merged thread reader. Three shapes share it, all returning the same
     * ascending `{ data, nextCursor, prevCursor }` envelope:
     *
     *   - `before`  — page towards older messages (the default thread read),
     *   - `after`   — page towards newer messages, so a thread opened at an
     *                 anchor can scroll back down,
     *   - `anchor`  — one window centred on a message, for jumping to a search
     *                 hit outside the loaded range.
     *
     * `q` filters to matching messages. `anchor` cannot be combined with the
     * cursors or `q` — the server rejects that with a 400.
     */
    listMessages: (
        unifiedId: string,
        opts: {
            readonly before?: string;
            readonly after?: string;
            readonly anchor?: string;
            readonly q?: string;
            /** Restrict to one channel — search follows the open thread tab. */
            readonly channel?: MessageChannel;
            readonly limit?: number;
            /** Narrow to one email subject chain (Gmail-style thread view). */
            readonly emailThreadId?: string;
        } = {},
    ): Promise<IListMessagesResponse> =>
        apiClient
            .get<IApiResponse<IListMessagesResponse>>(
                `${ROOT}/unified-conversations/${unifiedId}/messages`,
                {
                    params: {
                        ...(opts.before ? { before: opts.before } : {}),
                        ...(opts.after ? { after: opts.after } : {}),
                        ...(opts.anchor ? { anchor: opts.anchor } : {}),
                        ...(opts.q ? { q: opts.q } : {}),
                        ...(opts.channel ? { channel: opts.channel } : {}),
                        ...(opts.limit ? { limit: opts.limit } : {}),
                        ...(opts.emailThreadId ? { emailThreadId: opts.emailThreadId } : {}),
                    },
                },
            )
            .then((r) => r.data.data),

    /** Email subject chains of one contact, newest activity first. */
    listEmailThreads: (unifiedId: string): Promise<{ data: IEmailThread[] }> =>
        apiClient
            .get<
                IApiResponse<{ data: IEmailThread[] }>
            >(`${ROOT}/unified-conversations/${unifiedId}/email-threads`)
            .then((r) => r.data.data),

    /**
     * Outbound send. Idempotent on `clientMessageId` — replaying the same body
     * returns the EXISTING row's id with `replay: true`.
     *
     * Returns an ACK, not a message. This was typed `Promise<IMessage>` for
     * months; see `ISendMessageAck` for what that cost.
     */
    sendMessage: (unifiedId: string, body: ISendMessageInput): Promise<ISendMessageAck> =>
        apiClient
            .post<
                IApiResponse<ISendMessageAck>
            >(`${ROOT}/unified-conversations/${unifiedId}/messages`, body)
            .then((r) => r.data.data),

    /**
     * Schedule an outbound message for a future time. Reuses the optimistic
     * idempotency key (`clientMessageId`); replaying returns the existing row.
     */
    scheduleMessage: (unifiedId: string, body: IScheduleMessageInput): Promise<ISendMessageAck> =>
        apiClient
            .post<
                IApiResponse<ISendMessageAck>
            >(`${ROOT}/unified-conversations/${unifiedId}/messages/schedule`, body)
            .then((r) => r.data.data),

    /** Update a scheduled message's fire time and/or body. */
    rescheduleMessage: (
        messageId: string,
        body: IRescheduleMessageInput,
    ): Promise<{ id: string }> =>
        apiClient
            .patch<IApiResponse<{ id: string }>>(`${ROOT}/messages/${messageId}/schedule`, body)
            .then((r) => r.data.data),

    /** Send a scheduled message immediately instead of waiting for its time. */
    sendScheduledNow: (messageId: string): Promise<{ id: string }> =>
        apiClient
            .post<IApiResponse<{ id: string }>>(`${ROOT}/messages/${messageId}/send-now`, {})
            .then((r) => r.data.data),

    /** Cancel a scheduled message before it fires. */
    cancelScheduledMessage: (messageId: string): Promise<{ id: string }> =>
        apiClient
            .delete<IApiResponse<{ id: string }>>(`${ROOT}/messages/${messageId}/schedule`)
            .then((r) => r.data.data),

    /** Fire every scheduled message in a conversation now. */
    sendAllScheduled: (unifiedId: string): Promise<{ count: number }> =>
        apiClient
            .post<
                IApiResponse<{ count: number }>
            >(`${ROOT}/unified-conversations/${unifiedId}/scheduled-messages/send-now`, {})
            .then((r) => r.data.data),

    /** Cancel every scheduled message in a conversation. */
    cancelAllScheduled: (unifiedId: string): Promise<{ count: number }> =>
        apiClient
            .delete<
                IApiResponse<{ count: number }>
            >(`${ROOT}/unified-conversations/${unifiedId}/scheduled-messages`)
            .then((r) => r.data.data),

    sendInternalNote: (
        unifiedId: string,
        body: ISendInternalNoteInput,
    ): Promise<IConversationNote> =>
        apiClient
            .post<
                IApiResponse<IConversationNote>
            >(`${ROOT}/unified-conversations/${unifiedId}/notes`, body)
            .then((r) => r.data.data),

    /**
     * Eligible teammates for the @-mention dropdown — org members who can create
     * notes (`INBOX_REPLY`). Optional `search` filters by name/email.
     */
    getMentionCandidates: (search?: string): Promise<IMentionCandidate[]> =>
        apiClient
            .get<IApiResponse<IMentionCandidate[]>>(`${ROOT}/note-mention-candidates`, {
                params: search ? { search } : undefined,
            })
            .then((r) => r.data.data),

    /**
     * Request a presigned PUT URL to upload a note attachment directly to S3.
     * Scoped to the conversation so the storage key groups by it. The returned
     * `storageKey` is echoed back in the note-create payload.
     */
    getNoteAttachmentUploadUrl: (
        unifiedConversationId: string,
        input: {
            filename: string;
            contentType: string;
            sizeBytes: number;
        },
    ): Promise<INoteAttachmentUploadUrl> =>
        apiClient
            .post<
                IApiResponse<INoteAttachmentUploadUrl>
            >(`${ROOT}/unified-conversations/${unifiedConversationId}/note-attachments/presigned-upload`, input)
            .then((r) => r.data.data),

    /**
     * Presigned PUT URL for an outbound EMAIL attachment. Unlike note
     * attachments this is not conversation-scoped — the compose may not have a
     * conversation yet. The returned `storageKey` is echoed back on the send
     * payload's `attachments` (as `mimeType`, per `IStagedEmailAttachmentInput`).
     */
    presignEmailAttachment: (input: {
        filename: string;
        contentType: string;
        sizeBytes: number;
    }): Promise<IEmailAttachmentUploadUrl> =>
        apiClient
            .post<
                IApiResponse<IEmailAttachmentUploadUrl>
            >(`${ROOT}/email/attachments/presign`, input)
            .then((r) => r.data.data),

    /** Mint a short-lived presigned GET URL for a saved note attachment. */
    getNoteAttachmentPresignedUrl: (attachmentId: string): Promise<{ url: string }> =>
        apiClient
            .get<IApiResponse<{ url: string }>>(
                `${ROOT}/note-attachments/${attachmentId}/presigned-url`,
                // The client sets no default timeout, so without this a stalled
                // request hangs forever and the preview spinner with it.
                { timeout: NOTE_ATTACHMENT_PREVIEW_TIMEOUT_MS },
            )
            .then((r) => r.data.data),

    /**
     * Ask the server to render a note attachment it knows how to convert
     * (DOCX, XLSX, CSV). Everything the browser can open by itself is rendered
     * from the presigned URL instead and never calls this.
     */
    getNoteAttachmentPreview: (attachmentId: string): Promise<INoteAttachmentPreview> =>
        apiClient
            .get<IApiResponse<INoteAttachmentPreview>>(
                `${ROOT}/note-attachments/${attachmentId}/preview`,
                // Same reason as the presigned-URL call above: `apiClient` sets
                // no default timeout and axios defaults to none, so a stalled
                // request would leave the dialog spinning forever.
                { timeout: NOTE_ATTACHMENT_PREVIEW_TIMEOUT_MS },
            )
            .then((r) => r.data.data),

    /**
     * Edit a note's body. Author-only on the server — non-authors get a 404
     * so existence isn't leaked.
     */
    updateInternalNote: (noteId: string, body: IUpdateInternalNoteInput): Promise<{ id: string }> =>
        apiClient
            .patch<IApiResponse<{ id: string }>>(`${ROOT}/notes/${noteId}`, body)
            .then((r) => r.data.data),

    /**
     * Soft-delete a note. Author-only on the server. WS broadcasts so other
     * agents' caches drop the row without a refetch.
     */
    deleteInternalNote: (noteId: string): Promise<{ id: string }> =>
        apiClient
            .delete<IApiResponse<{ id: string }>>(`${ROOT}/notes/${noteId}`)
            .then((r) => r.data.data),

    /**
     * Mark a per-channel `Conversation` as read. The server zeroes the child's
     * `unread_count` and recomputes the unified parent's aggregate inside one
     * transaction; the result is broadcast over `inbox:conversation:read`.
     */
    markConversationRead: (conversationId: string): Promise<{ id: string }> =>
        apiClient
            .patch<IApiResponse<{ id: string }>>(`${ROOT}/conversations/${conversationId}/read`, {})
            .then((r) => r.data.data),

    /**
     * Zero ONE email subject chain's unread badge. Separate from
     * `markConversationRead`: a conversation aggregates every chain on the EMAIL
     * channel, and reading one says nothing about the rest. Idempotent — the
     * server no-ops a thread already at zero.
     */
    markEmailThreadRead: (emailThreadId: string): Promise<{ id: string }> =>
        apiClient
            .patch<IApiResponse<{ id: string }>>(`${ROOT}/email-threads/${emailThreadId}/read`, {})
            .then((r) => r.data.data),

    pinUnified: (id: string, pinned: boolean): Promise<IUnifiedConversation> => {
        const body: IPinUnifiedInput = { isPinned: pinned };
        return apiClient
            .patch<
                IApiResponse<IUnifiedConversation>
            >(`${ROOT}/unified-conversations/${id}/pin`, body)
            .then((r) => r.data.data);
    },

    /** Per-user: mutes only the current user's notifications for this thread. */
    muteUnified: (id: string, muted: boolean): Promise<{ id: string; isMuted: boolean }> => {
        const body: IMuteUnifiedInput = { isMuted: muted };
        return apiClient
            .patch<
                IApiResponse<{ id: string; isMuted: boolean }>
            >(`${ROOT}/unified-conversations/${id}/mute`, body)
            .then((r) => r.data.data);
    },

    archiveUnified: (id: string, archived: boolean): Promise<IUnifiedConversation> => {
        const body: IArchiveUnifiedInput = { isArchived: archived };
        return apiClient
            .patch<
                IApiResponse<IUnifiedConversation>
            >(`${ROOT}/unified-conversations/${id}/archive`, body)
            .then((r) => r.data.data);
    },

    assignUnified: (id: string, assignedTo: string | null): Promise<IUnifiedConversation> => {
        const body: IAssignUnifiedInput = { assignedTo };
        return apiClient
            .patch<
                IApiResponse<IUnifiedConversation>
            >(`${ROOT}/unified-conversations/${id}/assign`, body)
            .then((r) => r.data.data);
    },

    /**
     * Lifecycle status change. Unlike its siblings above this is typed for what
     * the server actually sends back — `{ id, status }`, not the conversation.
     * The authoritative row arrives moments later on `inbox:unified:updated`.
     *
     * The server enforces the transition graph; an illegal pair comes back 400,
     * as does SNOOZED without a future `snoozedUntil` (or with one elsewhere).
     */
    setStatusUnified: (
        id: string,
        body: ISetStatusUnifiedInput,
    ): Promise<{ id: string; status: ConversationStatus }> => {
        return apiClient
            .patch<
                IApiResponse<{ id: string; status: ConversationStatus }>
            >(`${ROOT}/unified-conversations/${id}/status`, body)
            .then((r) => r.data.data);
    },

    /**
     * Reconnection sync. Server replies with `{ tooStale: true }` if the gap
     * exceeds the replay window (~5 min) → caller must invalidate the cache.
     */
    sync: (since: string): Promise<ISyncResponse> => {
        const body: ISyncRequestInput = { since };
        return apiClient
            .post<IApiResponse<ISyncResponse>>(`${ROOT}/sync`, body)
            .then((r) => r.data.data);
    },

    /**
     * Rewrite or compose the composer body with the org's AI.
     *
     * CANCELLATION CONVENTION — this is the first call in the codebase to take
     * one, so it sets the shape: long-running, user-abandonable requests accept
     * a trailing `signal?: AbortSignal` and forward it to axios. An abort
     * rejects with an error for which `axios.isCancel(err)` is true, and
     * callers MUST check that before surfacing anything — otherwise merely
     * switching conversation mid-request shows the user a failure.
     *
     * Served by `AiDraftingController` (its own module), not `InboxController`,
     * despite the shared `/inbox` prefix.
     */
    draft: (body: IAiDraftInput, signal?: AbortSignal): Promise<IAiDraftResult> =>
        apiClient
            .post<IApiResponse<IAiDraftResult>>(`${ROOT}/ai-draft`, body, { signal })
            .then((r) => r.data.data),
    // ── Compose-new (modal composers) ─────────────────────────────────────

    /** New email conversation: one email, all recipients on it; threads under
     *  the first To contact (created if unknown). Idempotent on clientMessageId. */
    composeEmail: (body: IComposeEmailInput): Promise<IComposeEmailResult> =>
        apiClient
            .post<IApiResponse<IComposeEmailResult>>(`${ROOT}/email/compose`, body)
            .then((r) => r.data.data),

    /** SMS fan-out: one SMS per contact (≤50), per-contact result entries.
     *  Idempotent per contact via uuidv5(contactId, clientMessageId) server-side. */
    composeSms: (body: IComposeSmsInput): Promise<IComposeSmsResult> =>
        apiClient
            .post<IApiResponse<IComposeSmsResult>>(`${ROOT}/sms/compose`, body)
            .then((r) => r.data.data),

    /** WhatsApp fan-out (Task 11): one WhatsApp message per contact (≤50,
     *  contact ids ONLY — no raw numbers), per-contact result entries.
     *  Idempotent per contact via uuidv5(contactId, clientMessageId) server-side. */
    composeWhatsApp: (body: IComposeWhatsAppInput): Promise<IComposeWhatsAppResult> =>
        apiClient
            .post<IApiResponse<IComposeWhatsAppResult>>(`${ROOT}/whatsapp/compose`, body)
            .then((r) => r.data.data),

    // ── Email attachments ─────────────────────────────────────────────────

    /** Fresh presigned GET URLs for a message's attachments (metadata rides the
     *  message row; URLs are minted on demand because they expire). */
    getMessageAttachments: (messageId: string): Promise<IMessageAttachment[]> =>
        apiClient
            .get<IApiResponse<IMessageAttachment[]>>(`${ROOT}/messages/${messageId}/attachments`)
            .then((r) => r.data.data),

    // ── WhatsApp message reactions ────────────────────────────────────────

    /** Sets the workspace's reaction, replacing whatever was in the slot. */
    reactToMessage: (messageId: string, emoji: string): Promise<{ id: string }> =>
        apiClient
            .put<IApiResponse<{ id: string }>>(`${ROOT}/messages/${messageId}/reaction`, { emoji })
            .then((r) => r.data.data),

    /** Clears the workspace's reaction. A no-op server-side when there is none. */
    removeMessageReaction: (messageId: string): Promise<{ id: string }> =>
        apiClient
            .delete<IApiResponse<{ id: string }>>(`${ROOT}/messages/${messageId}/reaction`)
            .then((r) => r.data.data),

    /** Every conversation the current user muted — ids only. */
    listMutedConversationIds: (): Promise<readonly string[]> =>
        apiClient
            .get<IApiResponse<string[]>>(`${ROOT}/muted-conversation-ids`)
            .then((r) => r.data.data),

    // ── Message stars (per user, any channel) ─────────────────────────────

    /** Stars a message for the current user. Idempotent server-side. */
    starMessage: (messageId: string): Promise<{ id: string }> =>
        apiClient
            .put<IApiResponse<{ id: string }>>(`${ROOT}/messages/${messageId}/star`)
            .then((r) => r.data.data),

    /** Unstars a message for the current user. A no-op when it is not starred. */
    unstarMessage: (messageId: string): Promise<{ id: string }> =>
        apiClient
            .delete<IApiResponse<{ id: string }>>(`${ROOT}/messages/${messageId}/star`)
            .then((r) => r.data.data),

    /** The current user's starred messages in one conversation, newest star first. */
    listStarredMessages: (unifiedId: string): Promise<readonly IStarredMessage[]> =>
        apiClient
            .get<
                IApiResponse<IStarredMessage[]>
            >(`${ROOT}/unified-conversations/${unifiedId}/starred-messages`)
            .then((r) => r.data.data),

    // ── Email drafts (client-minted UUID id, PUT-upserted) ────────────────

    listEmailDrafts: (params?: { unifiedConversationId?: string }): Promise<IEmailDraft[]> =>
        apiClient
            .get<IApiResponse<IEmailDraft[]>>(`${ROOT}/email/drafts`, {
                params: params?.unifiedConversationId
                    ? { unifiedConversationId: params.unifiedConversationId }
                    : undefined,
            })
            .then((r) => r.data.data),

    upsertEmailDraft: (
        id: string,
        body: IUpsertEmailDraftInput,
    ): Promise<{ id: string; updatedAt: string }> =>
        apiClient
            .put<
                IApiResponse<{ id: string; updatedAt: string }>
            >(`${ROOT}/email/drafts/${id}`, body)
            .then((r) => r.data.data),

    deleteEmailDraft: (id: string): Promise<{ id: string }> =>
        apiClient
            .delete<IApiResponse<{ id: string }>>(`${ROOT}/email/drafts/${id}`)
            .then((r) => r.data.data),

    // ── AI assists ────────────────────────────────────────────────────────

    /**
     * Write a subject line from the drafted body (the magic-wand button).
     * Costs AI credits — the server rejects a request with nothing drafted.
     *
     * Send `text`, not `html`: the model only ever reads text, so handing over
     * markup meant uploading a whole compiled template per press. `html`
     * remains for any caller that genuinely only holds markup.
     *
     * Takes a `signal` per the cancellation convention on `draft` above —
     * callers MUST check `axios.isCancel(err)` before surfacing a failure.
     */
    suggestEmailSubject: (
        body: { text?: string; html?: string; subject?: string },
        signal?: AbortSignal,
    ): Promise<{ subject: string }> =>
        apiClient
            .post<IApiResponse<{ subject: string }>>(`${ROOT}/email/subject-suggest`, body, {
                signal,
            })
            .then((r) => r.data.data),

    /** The stored insight for a conversation; `null` if none was ever generated. */
    getConversationInsight: (unifiedId: string): Promise<IConversationInsight | null> =>
        apiClient
            .get<
                IApiResponse<IConversationInsight | null>
            >(`${ROOT}/unified-conversations/${unifiedId}/insight`)
            .then((r) => r.data.data),

    /** Ask for a fresh insight. Costs AI credits; the server declines when the
     *  stored one is still current, returning it unchanged. */
    refreshConversationInsight: (unifiedId: string): Promise<IConversationInsight> =>
        apiClient
            .post<
                IApiResponse<IConversationInsight>
            >(`${ROOT}/unified-conversations/${unifiedId}/insight/refresh`)
            .then((r) => r.data.data),
};
